import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useCallback } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyPage from "./pages/VerifyPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LobbyPage from "./pages/LobbyPage";
import ChatRoomPage from "./pages/ChatRoomPage";
import ProfilePage from "./pages/ProfilePage";
import PrivateMessageWindow from "./components/PrivateMessageWindow";
import MessengerPage from "./pages/MessengerPage";
import InstallMessengerPage from "./pages/InstallMessengerPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import { TranslationProvider } from "./contexts/TranslationContext";
import GlobalPMNotifications from "./components/GlobalPMNotifications";
import { tokenStore, isPwaStandalone } from "./utils/tokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

export const AuthContext = createContext(null);

// Global PM Context - PMs persist across room navigation so voice stays connected
export const PMContext = createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Global PM windows state - persists across room navigation AND across hard refreshes via sessionStorage
  const [globalPMs, setGlobalPMs] = useState(() => {
    try {
      const saved = sessionStorage.getItem("globalPMs");
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });

  // Persist whenever globalPMs changes
  useEffect(() => {
    try { sessionStorage.setItem("globalPMs", JSON.stringify(globalPMs)); } catch (_) { /* noop */ }
  }, [globalPMs]);

  // Track the *visible* viewport height (excludes the on-screen keyboard) as --app-vh so
  // mobile chat/IM layouts can pin their input box above the keyboard instead of behind it.
  useEffect(() => {
    const setVH = () => {
      const h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      document.documentElement.style.setProperty("--app-vh", `${h}px`);
    };
    setVH();
    window.visualViewport?.addEventListener("resize", setVH);
    window.visualViewport?.addEventListener("scroll", setVH);
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);
    return () => {
      window.visualViewport?.removeEventListener("resize", setVH);
      window.visualViewport?.removeEventListener("scroll", setVH);
      window.removeEventListener("resize", setVH);
      window.removeEventListener("orientationchange", setVH);
    };
  }, []);
  
  // Initialize token from tokenStore.
  // - Normal browser: sessionStorage (clears on tab close, must re-login each visit)
  // - Installed PWA (standalone display-mode): localStorage (persists across app launches
  //   so the "downloadable Messenger app" feels like a real phone app)
  const [token, setToken] = useState(() => {
    // In non-standalone (browser) mode we still clear the stale localStorage token as a safety net
    if (!isPwaStandalone()) {
      try { localStorage.removeItem("token"); } catch (_e) { /* noop */ }
    }
    const t = tokenStore.get();
    console.log("Init token from tokenStore:", t ? "found" : "none", "standalone=", isPwaStandalone());
    return t;
  });

  // Global PM management functions
  const openPMWindow = useCallback((userId, username) => {
    setGlobalPMs(prev => {
      if (prev.find(pm => pm.user_id === userId)) {
        return prev; // Already open
      }
      return [...prev, { user_id: userId, username }];
    });
  }, []);

  const closePMWindow = useCallback((userId) => {
    setGlobalPMs(prev => prev.filter(pm => pm.user_id !== userId));
  }, []);

  // Logout function that also calls the backend
  const logout = useCallback(async (callApi = true) => {
    if (callApi && token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Logout API error:", error);
      }
    }
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("globalPMs");
    tokenStore.clear();
    setGlobalPMs([]);
    setToken(null);
    setUser(null);
  }, [token]);

  // Auto-logout when page is closed — but only in normal browser mode.
  // In installed PWA (standalone) we keep the session so tapping the home-screen
  // icon again lands the user right back inside Messenger (like a real app).
  useEffect(() => {
    if (isPwaStandalone()) return; // skip auto-logout for installed PWA
    const handleBeforeUnload = () => {
      if (token) {
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon(`${API}/auth/logout?token=${token}`, blob);
        sessionStorage.removeItem("token");
      }
    };

    const handlePageHide = () => {
      if (token) {
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        navigator.sendBeacon(`${API}/auth/logout?token=${token}`, blob);
        sessionStorage.removeItem("token");
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth check failed:", error);
          tokenStore.clear();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = (newToken, userData) => {
    tokenStore.set(newToken);
    setToken(newToken);
    setUser(userData);
  };

  // Update user status
  const updateStatus = async (statusMessage) => {
    if (!token) return;
    try {
      await axios.put(`${API}/auth/status`, 
        { status_message: statusMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(prev => ({ ...prev, status_message: statusMessage }));
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#C0C0C0] flex items-center justify-center">
        <div className="retro-window p-4">
          <div className="retro-title-bar">
            <span>Loading...</span>
          </div>
          <div className="p-4 bg-white">
            <p className="font-verdana text-sm">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateStatus, setToken, setUser }}>
      <PMContext.Provider value={{ globalPMs, openPMWindow, closePMWindow }}>
        <TranslationProvider>
          <Toaster position="top-center" />
          <BrowserRouter>
            {/* Global notification subscriber — runs on every authenticated route */}
            {user && <GlobalPMNotifications />}
            {/* App-wide containment: no route may overflow the viewport horizontally */}
            <div className="w-full max-w-[100vw] overflow-x-hidden">
            <Routes>
              <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
              <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
              <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/" />} />
              <Route path="/" element={user ? <LobbyPage /> : <Navigate to="/login" />} />
              <Route path="/room/:roomId" element={user ? <ChatRoomPage /> : <Navigate to="/login" />} />
              <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
              <Route path="/profile/:userId" element={user ? <ProfilePage /> : <Navigate to="/login" />} />
              <Route path="/messenger" element={user ? <MessengerPage /> : (() => {
                const sp = new URLSearchParams(window.location.search);
                sp.set("app", "messenger");
                return <Navigate to={`/login?${sp.toString()}`} replace />;
              })()} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route path="/install" element={<InstallMessengerPage />} />
              <Route path="/get-messenger" element={<InstallMessengerPage />} />
            </Routes>
            </div>

            {/* Global PM Windows - inside the Router so PrivateMessageWindow can use
                useNavigate() (fixes white-screen crash on opening an IM). Persist across
                navigation so voice/video stays connected. */}
            {user && globalPMs.map((pm, index) => (
              <PrivateMessageWindow
                key={pm.user_id}
                targetUserId={pm.user_id}
                targetUsername={pm.username}
                onClose={() => closePMWindow(pm.user_id)}
                style={{ top: 100 + index * 30, left: 100 + index * 30 }}
              />
            ))}
          </BrowserRouter>
        </TranslationProvider>
      </PMContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
